import { useMemo, useState } from 'react'
import { CartesianGrid, LabelList, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import type { SponsorStatement } from './economicsModel'
import {
  buildSeries,
  coverageOf,
  indexed,
  kpisOf,
  periodChange,
  shortLabel,
  type ChartSeries,
  type Kpi,
} from './sponsorStatementChartModel'

const AXIS = '#a8a29e'
const GRID = 'rgba(255,255,255,0.05)'

type Mode = 'amounts' | 'growth'

function money(value: number): string {
  const negative = value < 0
  const abs = Math.abs(value)
  const body = abs >= 1_000_000 ? `$${(abs / 1_000_000).toFixed(2)}M` : `$${Math.round(abs).toLocaleString('en-US')}`
  return negative ? `(${body})` : body
}

function compact(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

function signedPct(value: number): string {
  const text = `${(Math.abs(value) * 100).toFixed(1)}%`
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text
}

/**
 * The Sponsor's Operating Statement, as an underwriter reads it: how revenue,
 * expenses, debt service and NOI move together, the growth and margins they
 * imply, and whether coverage holds — every figure the workbook's own, or a
 * ratio of two of them, said as such.
 *
 * Two measures of different scale are never put on one axis: dollars draw on
 * the main chart, coverage on its own beneath or beside it.
 */
export function SponsorStatementChart({ statement, fill = false }: { statement: SponsorStatement; fill?: boolean }) {
  const { money: allSeries, overflow, defaults } = useMemo(() => buildSeries(statement), [statement])
  const kpis = useMemo(() => kpisOf(statement), [statement])
  const coverage = useMemo(() => coverageOf(statement), [statement])
  const [visible, setVisible] = useState<string[]>(defaults)
  const [mode, setMode] = useState<Mode>('amounts')
  const shown = allSeries.filter((s) => visible.includes(s.id))
  const firstPeriod = statement.periods[0] ?? 'first period'

  const rows = useMemo(() => statement.periods.map((period, i) => {
    const row: Record<string, number | string | null> = { period }
    for (const s of allSeries) {
      row[s.id] = mode === 'growth' ? indexed(s.values)[i] : s.values[i]
      row[`${s.id}__raw`] = s.values[i]
    }
    return row
  }), [statement.periods, allSeries, mode])

  const toggle = (id: string) => setVisible((now) => (now.includes(id) ? now.filter((v) => v !== id) : [...now, id]))
  const lastIndex = statement.periods.length - 1
  const directLabels = shown.length <= 4
  const mainHeight = fill ? 300 : 170
  const coverageHeight = fill ? 300 : 120
  // End-of-line labels are placed in series order within one render; a label
  // that would print over one already placed moves to the nearest free line, so
  // lines that end together (three lines all growing 3%) stay readable.
  const placedLabels: number[] = []
  const placeLabel = (y: number): number => {
    // Kept inside the plot: clear of the top edge and of the period axis.
    const top = 10
    const bottom = mainHeight - 34
    const clamp = (v: number) => Math.min(bottom, Math.max(top, v))
    const free = (v: number) => !placedLabels.some((taken) => Math.abs(taken - v) < 11)
    let at = clamp(y)
    for (let step = 1; !free(at) && step < 12; step += 1) {
      // Nearest free slot, alternating below and above.
      const offset = Math.ceil(step / 2) * 11 * (step % 2 ? 1 : -1)
      const candidate = clamp(y + offset)
      if (free(candidate)) at = candidate
    }
    placedLabels.push(at)
    return at
  }

  if (!allSeries.length && !coverage) {
    return <p className="py-6 text-[10px] text-stone-400">The statement states no dollar line or coverage ratio to chart; the Table view shows every line as stated.</p>
  }

  return (
    <div data-testid="sponsor-statement-chart" className="flex min-h-0 flex-col gap-2">
      {kpis.length > 0 && (
        <div role="list" aria-label="Figures derived from the sponsor's statement" className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 xl:grid-cols-5">
          {kpis.map((kpi) => <KpiTile key={kpi.id} kpi={kpi} />)}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px]">
        <div role="group" aria-label="Chart scale" className="flex overflow-hidden rounded border border-stone-800">
          {(['amounts', 'growth'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
              className={`px-2 py-1 ${mode === m ? 'bg-amber-300/[0.08] text-amber-200' : 'text-stone-400 hover:text-stone-200'}`}
            >
              {m === 'amounts' ? 'Amounts' : `Growth (${firstPeriod} = 100)`}
            </button>
          ))}
        </div>
        <div role="group" aria-label="Statement lines shown" className="flex flex-wrap items-center gap-1.5">
          {allSeries.map((s) => {
            const on = visible.includes(s.id)
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(s.id)}
                className={`flex items-center gap-1.5 rounded border px-2 py-0.5 ${on ? 'border-stone-600 text-stone-200' : 'border-stone-800 text-stone-500'}`}
              >
                <span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ background: on ? s.color : 'transparent', boxShadow: `inset 0 0 0 1.5px ${s.color}` }} />
                {s.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className={`grid min-h-0 gap-2 ${fill && coverage ? 'xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : ''}`}>
        {allSeries.length > 0 && (
          <figure className="min-w-0">
            <figcaption className="mb-1 font-mono text-[8px] uppercase tracking-widest text-stone-500">
              {mode === 'amounts' ? 'Revenue, expenses, debt service and NOI · as stated' : `Each line indexed to its ${firstPeriod} value`}
            </figcaption>
            {shown.length === 0 ? (
              <p className="py-6 text-[10px] text-stone-400">Select a line above to chart it.</p>
            ) : (
              <div style={{ height: mainHeight }} role="img" aria-label={`Line chart of ${shown.map((s) => s.label).join(', ')} by period`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rows} margin={{ top: 12, right: directLabels ? 132 : 16, bottom: 4, left: 4 }}>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="period" tick={{ fill: AXIS, fontSize: 9 }} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={18} />
                    <YAxis
                      width={56}
                      tick={{ fill: AXIS, fontSize: 9 }}
                      tickLine={false}
                      axisLine={false}
                      domain={mode === 'growth' ? ['auto', 'auto'] : [0, 'auto']}
                      tickFormatter={(v: number) => (mode === 'growth' ? `${Math.round(v)}` : compact(v))}
                    />
                    {mode === 'growth' && <ReferenceLine y={100} stroke="#57534e" strokeDasharray="3 3" />}
                    <Tooltip
                      cursor={{ stroke: '#57534e', strokeWidth: 1 }}
                      content={({ active, label, payload }) => (active && payload?.length ? (
                        <StatementTooltip label={String(label)} index={statement.periods.indexOf(String(label))} series={shown} mode={mode} />
                      ) : null)}
                    />
                    {shown.map((s) => (
                      <Line
                        key={s.id}
                        dataKey={s.id}
                        name={s.label}
                        stroke={s.color}
                        strokeWidth={s.key === 'noi' ? 2.75 : 2}
                        dot={{ r: 3.5, strokeWidth: 2, stroke: '#0d0d0d', fill: s.color }}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: '#0d0d0d' }}
                        connectNulls={false}
                        isAnimationActive={false}
                      >
                        {directLabels && (
                          <LabelList
                            dataKey={s.id}
                            content={(props) => {
                              const { x, y, index } = props as { x?: number; y?: number; index?: number }
                              if (index !== lastIndex || x == null || y == null) return null
                              const raw = s.values[lastIndex]
                              if (raw == null) return null
                              const text = mode === 'growth' ? `${Math.round(indexed(s.values)[lastIndex] ?? 0)}` : compact(raw)
                              return (
                                <text x={Number(x) + 8} y={placeLabel(Number(y))} dy={3} fontSize={9} fill="#d6d3d1">
                                  {shortLabel(s)} {text}
                                </text>
                              )
                            }}
                          />
                        )}
                      </Line>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </figure>
        )}

        {coverage && (fill || !allSeries.length) && (
          <figure className="min-w-0">
            <figcaption className="mb-1 font-mono text-[8px] uppercase tracking-widest text-stone-500">
              Debt service coverage · {coverage.basis === 'stated' ? 'as stated' : 'NOI ÷ debt service'}
            </figcaption>
            <div style={{ height: coverageHeight }} role="img" aria-label="Line chart of debt service coverage by period, with the 1.00x break-even line">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={coverage.points} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="period" tick={{ fill: AXIS, fontSize: 9 }} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={18} />
                  <YAxis
                    width={36}
                    tick={{ fill: AXIS, fontSize: 9 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[(min: number) => Math.min(0.8, Math.floor(min * 10) / 10), (max: number) => Math.ceil(max * 10) / 10 + 0.1]}
                    tickFormatter={(v: number) => `${v.toFixed(1)}x`}
                  />
                  <ReferenceLine y={1} stroke="#a8a29e" strokeDasharray="4 3" label={{ value: '1.00x break-even', position: 'insideBottomRight', fill: '#a8a29e', fontSize: 9 }} />
                  <Tooltip
                    cursor={{ stroke: '#57534e', strokeWidth: 1 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null
                      const point = coverage.points.find((p) => p.period === label)
                      if (!point || point.dscr == null) return null
                      const i = coverage.points.indexOf(point)
                      return (
                        <div className="rounded border border-amber-400/20 bg-[#0b0b0b] px-2 py-1.5 text-[10px] text-stone-200 shadow-lg">
                          <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-stone-400">{label}</div>
                          <div className="font-mono text-[12px] text-stone-100">{point.dscr.toFixed(2)}x</div>
                          {point.noi != null && <div className="text-stone-400">NOI {money(point.noi)}</div>}
                          {point.debtService != null && <div className="text-stone-400">Debt service {money(point.debtService)}</div>}
                          <div className="mt-0.5 text-stone-500">{coverage.basis === 'stated' ? (coverage.cell(i) ?? 'Cell not recorded') : 'Computed: NOI ÷ debt service'}</div>
                        </div>
                      )
                    }}
                  />
                  <Line dataKey="dscr" name="DSCR" stroke="#e7e5e4" strokeWidth={2} dot={{ r: 3.5, strokeWidth: 2, stroke: '#0d0d0d', fill: '#e7e5e4' }} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false}>
                    <LabelList dataKey="dscr" position="top" offset={8} fontSize={9} fill="#d6d3d1" formatter={(v: unknown) => (typeof v === 'number' ? `${v.toFixed(2)}x` : '')} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </figure>
        )}
      </div>

      <p className="text-[9px] leading-relaxed text-stone-500">
        Values as stated in the workbook. Growth, margins and coverage are computed from those stated lines; a missing period stays a gap.
        {overflow > 0 ? ` ${overflow} further dollar ${overflow === 1 ? 'line is' : 'lines are'} in the Table view.` : ''}
      </p>
    </div>
  )
}

function KpiTile({ kpi }: { kpi: Kpi }) {
  return (
    <div role="listitem" className="min-w-0 rounded border border-white/[0.06] bg-white/[0.02] px-2 py-1.5">
      <div className="flex items-center justify-between gap-1">
        <span className="truncate font-mono text-[8px] uppercase tracking-widest text-stone-500">{kpi.label}</span>
        <span className="shrink-0 font-mono text-[7px] uppercase tracking-wider text-stone-600">{kpi.source}</span>
      </div>
      <div className={`mt-0.5 font-mono text-[15px] leading-tight ${kpi.tone === 'warn' ? 'text-amber-300' : 'text-stone-100'}`}>
        {kpi.tone === 'warn' && <span className="sr-only">Below break-even: </span>}
        {kpi.value}
      </div>
      <div className="truncate text-[9px] text-stone-500" title={kpi.basis}>{kpi.basis}</div>
    </div>
  )
}

function StatementTooltip({ label, index, series, mode }: { label: string; index: number; series: ChartSeries[]; mode: Mode }) {
  return (
    <div className="min-w-[200px] rounded border border-amber-400/20 bg-[#0b0b0b] px-2 py-1.5 text-[10px] text-stone-200 shadow-lg">
      <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-stone-400">{label}</div>
      {series.map((s) => {
        const raw = s.values[index]
        const change = periodChange(s.values, index)
        const idx = indexed(s.values)[index]
        return (
          <div key={s.id} className="flex items-center justify-between gap-3 py-0.5">
            <span className="flex min-w-0 items-center gap-1.5">
              <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="truncate text-stone-300">{s.label}</span>
            </span>
            <span className="shrink-0 text-right font-mono">
              {raw == null ? <span className="text-stone-500">Not stated</span> : (
                <>
                  <span className="text-stone-100">{money(raw)}</span>
                  {mode === 'growth' && idx != null && <span className="ml-1.5 text-stone-400">{Math.round(idx)}</span>}
                  {change != null && <span className="ml-1.5 text-stone-500">{signedPct(change)}</span>}
                </>
              )}
            </span>
          </div>
        )
      })}
      <div className="mt-1 text-[9px] text-stone-500">Change is against the previous period.</div>
    </div>
  )
}
